<template>
  <div class="partners-page">
    <v-container>
      <h1 class="text-headline-small font-weight-bold mb-2">Nos partenaires</h1>
      <p class="text-body-medium text-medium-emphasis mb-6">
        Merci aux entreprises et aux organismes qui soutiennent Québec Eau Vive.
      </p>

      <template v-if="loading">
        <v-row>
          <v-col v-for="n in 4" :key="n" cols="12" sm="6" md="3">
            <v-skeleton-loader type="image" height="100" />
          </v-col>
        </v-row>
      </template>

      <template v-else-if="payingPartners.length">
        <h2 class="partners-heading text-body-large font-weight-medium text-uppercase text-medium-emphasis mb-3">
          Partenaires principaux
        </h2>
        <v-row class="mb-8">
          <v-col v-for="partner in payingPartners" :key="partner.id" cols="12" sm="6" md="4">
            <a
              :href="partner.url"
              target="_blank"
              rel="noopener noreferrer"
              class="partner-card paying"
              @click="logClick(partner.id)"
            >
              <div class="partner-logo-tile">
                <v-img :src="partner.image" :alt="partner.name" height="100" class="partner-img" />
              </div>
              <div class="partner-name mt-2 text-center text-body-medium font-weight-medium">{{ partner.name }}</div>
            </a>
          </v-col>
        </v-row>
      </template>

      <template v-if="freePartners.length">
        <h2 class="partners-heading text-body-large font-weight-medium text-uppercase text-medium-emphasis mb-3">
          Partenaires
        </h2>
        <v-row>
          <v-col v-for="partner in freePartners" :key="partner.id" cols="6" sm="4" md="3">
            <a
              :href="partner.url"
              target="_blank"
              rel="noopener noreferrer"
              class="partner-card"
              @click="logClick(partner.id)"
            >
              <div class="partner-logo-tile">
                <v-img :src="partner.image" :alt="partner.name" height="60" class="partner-img" />
              </div>
              <div class="partner-name mt-2 text-center text-body-small text-medium-emphasis">{{ partner.name }}</div>
            </a>
          </v-col>
        </v-row>
      </template>

      <v-empty-state
        v-if="!loading && !payingPartners.length && !freePartners.length"
        icon="mdi-handshake-outline"
        title="Aucun partenaire pour le moment"
        class="mt-12"
      ></v-empty-state>

      <v-sheet v-if="!loading" color="primary-tint" rounded="lg" class="become-partner mt-12 pa-6 text-center">
        <h2 class="text-title-large font-weight-bold mb-2">Devenir partenaire</h2>
        <p class="text-body-medium text-medium-emphasis mb-4">
          Vous souhaitez soutenir la communauté d'eau vive du Québec et faire connaître votre entreprise ou votre
          organisme? Écrivez-nous!
        </p>
        <!-- m.me opens a Messenger conversation with the Facebook page -->
        <v-btn
          href="https://m.me/quebecwhitewater"
          target="_blank"
          rel="noopener noreferrer"
          color="gold"
          variant="flat"
          prepend-icon="mdi-facebook-messenger"
        >
          Nous écrire sur Messenger
        </v-btn>
      </v-sheet>
    </v-container>
  </div>
</template>

<script>
  import firebase from "firebase/compat/app";
  import "firebase/compat/auth";
  import { mapState, mapActions } from "vuex";
  import { MOCK_PARTNERS } from "@/components/pages/river/components/partnersMock";
  import { signalPrerenderReady } from "@/utils/seo";

  export default {
    name: "Partners",
    data() {
      return {
        loading: true,
      };
    },
    computed: {
      ...mapState({
        storePartners: (state) => state.PartnersService.partners,
      }),
      allPartners() {
        if (process.env.NODE_ENV === "production") {
          return this.storePartners;
        }
        // In dev, pad with mocks so every scenario is visible
        const mockNeeded = MOCK_PARTNERS.filter((m) => !this.storePartners.some((p) => p.id === m.id));
        return [...this.storePartners, ...mockNeeded];
      },
      activePartners() {
        const today = new Date().toISOString().slice(0, 10);
        return this.allPartners.filter((p) => p.active && (!p.expiresAt || p.expiresAt >= today));
      },
      payingPartners() {
        return this.activePartners.filter((p) => p.isPaying);
      },
      freePartners() {
        return this.activePartners.filter((p) => !p.isPaying);
      },
    },
    async created() {
      await this.fetchPartners();
      this.loading = false;
      this.$nextTick(() => {
        signalPrerenderReady();
      });
    },
    methods: {
      ...mapActions("PartnersService", ["fetchPartners"]),
      async logClick(partnerId) {
        try {
          const user = firebase.auth().currentUser;
          const userInfo = user
            ? { id: user.uid, name: user.displayName || "Anonymous", email: user.email }
            : { id: "anonymous", name: "Anonymous", email: null };
          await this.$store.dispatch("PartnersService/logPartnerClick", { partnerId, userInfo });
        } catch (error) {
          console.error("Error logging partner click:", error.message);
        }
      },
    },
  };
</script>

<style scoped>
  .partners-page {
    padding-bottom: 48px;
  }

  .become-partner p {
    max-width: 560px;
    margin-left: auto;
    margin-right: auto;
  }

  .partners-heading {
    letter-spacing: 0.06em;
  }

  /* The card is a link: without an explicit colour the names would show in link blue / visited purple */
  .partner-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    padding: 8px;
    border-radius: 12px;
    color: rgb(var(--v-theme-on-surface));
    text-decoration: none;
  }

  .partner-card:focus-visible {
    outline: none;
  }

  /* Logos are made for a light background, so the tile stays white in both themes
     and only its border follows the theme (same as PartnersCaroussel.vue). */
  .partner-logo-tile {
    width: 100%;
    padding: 12px;
    border-radius: 8px;
    background: #fff;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    transition:
      transform 0.15s ease,
      box-shadow 0.15s ease;
  }

  .partner-card.paying .partner-logo-tile {
    padding: 16px;
  }

  .partner-card:hover .partner-logo-tile {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.16);
  }

  .partner-card:focus-visible .partner-logo-tile {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 2px;
  }

  .partner-card:hover .partner-name {
    color: rgb(var(--v-theme-primary));
  }

  @media (prefers-reduced-motion: reduce) {
    .partner-logo-tile {
      transition: none;
    }

    .partner-card:hover .partner-logo-tile {
      transform: none;
    }
  }

  .partner-img {
    width: 100%;
  }
</style>
